import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import {
  getPestAlertFrames,
  type PestRiskPestKey,
} from '../constants/pestRiskLayers';

export interface PestTimeseriesPoint {
  pestKey: PestRiskPestKey | string;
  pestLabel: string;
  quarter: string;
  quarterLabel: string;
  mean: number;
  min: number;
  max: number;
  validPercent: number;
}

export type FarmPestTimeseriesState =
  | { status: 'idle' }
  | { status: 'loading'; progress: number; total: number }
  | { status: 'loaded'; points: PestTimeseriesPoint[]; lastUpdated: string | null }
  | { status: 'unavailable' }
  | { status: 'no_coverage'; message: string }
  | { status: 'error'; message: string };

async function fetchPestStatForCog(
  cogUrl: string,
  featureBody: Record<string, unknown>,
  signal: AbortSignal
): Promise<{ mean: number; min: number; max: number; validPercent: number } | null> {
  const params = new URLSearchParams({ url: cogUrl, bidx: '1', nodata: 'nan' });
  const endpoint = `${DIGISAKA_API_ORIGIN}/api/statistics?${params.toString()}`;

  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(featureBody),
    signal,
  });

  if (!res.ok) return null;

  const data = await res.json();
  const featProps = data?.features?.[0]?.properties ?? data?.properties;
  const stats = featProps?.statistics?.b1 ?? featProps?.statistics?.['1'];
  if (!stats) return null;
  if (stats.mean == null) return null;
  if ((stats.valid_pixels ?? 0) <= 0 || (stats.valid_percent ?? 0) <= 0) return null;

  return {
    mean: stats.mean ?? 0,
    min: stats.min ?? 0,
    max: stats.max ?? 0,
    validPercent: stats.valid_percent ?? 0,
  };
}

/**
 * Browser TiTiler path for demo / unsaved fields — current-quarter alert pests only.
 */
export async function fetchPestAlertTimeseries(
  fieldGeoJson: Record<string, unknown>,
  onProgress: (progress: number, total: number) => void,
  signal: AbortSignal
): Promise<PestTimeseriesPoint[]> {
  const rawGeometry =
    (fieldGeoJson as Record<string, unknown>)?.type === 'Feature'
      ? (fieldGeoJson as Record<string, unknown>).geometry
      : fieldGeoJson;

  const featureBody = {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', geometry: rawGeometry, properties: {} }],
  };

  const frames = getPestAlertFrames();
  let completed = 0;
  const total = frames.length;

  if (total === 0) {
    onProgress(0, 0);
    return [];
  }

  const results = await Promise.all(
    frames.map(async ({ pestKey, pestLabel, frame }) => {
      try {
        const stat = await fetchPestStatForCog(frame.cogUrl, featureBody, signal);
        completed += 1;
        onProgress(completed, total);
        if (!stat) return null;
        return {
          pestKey,
          pestLabel,
          quarter: frame.quarter,
          quarterLabel: frame.label,
          mean: stat.mean,
          min: stat.min,
          max: stat.max,
          validPercent: stat.validPercent,
        } satisfies PestTimeseriesPoint;
      } catch {
        completed += 1;
        onProgress(completed, total);
        return null;
      }
    })
  );

  return results.filter((p): p is PestTimeseriesPoint => p != null && !Number.isNaN(p.mean));
}

export function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
